import videojs from 'video.js';

const AUDIO_ONLY_STREAM_DIMENSIONS = {
    x: 0,
    y: 0,
    width: 0,
    height: 0
};

export class MultistreamPluginStub extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        player.loader().onLoadPresentation(onLoadPresentation.bind(player));
    }
}

function sortByPlaybackAudioSource(a, b) {
    if (a.playbackAudioSource && !b.playbackAudioSource) {
        return -1;
    }
    if (b.playbackAudioSource && !a.playbackAudioSource) {
        return 1;
    }
    return 0;
}

function shouldLoad(presentationId, playCoverData, presentationData) {
    return (presentationData?.streams?.length ?? 0) > 1;
}

function findVisualItemBySourceId(composition, sourceId) {
    return composition.VisualItems.find(item => item.SourceId === sourceId);
}

function organizeStreams(composition, streams) {
    let organizedStreams = [];
    for (let i = 0; i < streams.length; i++) {
        let stream = streams[i];
        if (stream.audioOnly === true) {
            //AudioOnly streams are hidden, but kept in player for audio
            organizedStreams.push(Object.assign({}, stream, AUDIO_ONLY_STREAM_DIMENSIONS));
        } else if (!findVisualItemBySourceId(composition, stream.id)) {
            //Streams not in composition are not included
            continue;
        } else {
            //Streams in composition are assigned dimensions
            const visualItem = findVisualItemBySourceId(composition, stream.id);
            const streamLayoutInfo = {
                x: visualItem.TargetRect.X,
                y: visualItem.TargetRect.Y,
                width: visualItem.TargetRect.Width,
                height: visualItem.TargetRect.Height
            };
            organizedStreams.push(Object.assign({}, stream, streamLayoutInfo));
        }
    }
    return organizedStreams.sort(sortByPlaybackAudioSource);
}

async function onLoadPresentation(presentationId, playCoverData, presentationData) {
    const player = this;
    if (shouldLoad(...arguments)) {
        if (!videojs.getPlugin('multistream')) {
            const { MultistreamPlugin } = await import(/* webpackChunkName: 'multistream' */ './multistream.js');
            player.registerPlugin('multistream', MultistreamPlugin);
        }
        
        player.multistream().init();

        const map = presentationData.compositionMap;
        const comp = map?.VisualCompositions[0];
        let streams = presentationData.streams;

        //Filter streams absent from composition, hide audioonly streams, assign dimensions included streams
        const organizedStreams = organizeStreams(comp, streams);
        //loader needs presentationData to have organized streams when adding src to player
        presentationData.streams = organizedStreams;
        // Wait for each stream to load
        const multistreams = organizedStreams.slice(1);
        return Promise.all(multistreams.map(stream => {
            return player.multistream().add(stream);
        })).then(() => {
            // "Priority" property determines the order in which streams should appear on screen
            // PlaybackAudioSource determines which stream is the "main" one attached to the top-level player.
            // Set the priority of the main stream in the multistream plugin layout
            const { x, y, width, height } = organizedStreams[0];
            player.multistream().setMainStreamSize(x, y, width, height);
            
            const aspect = comp.Width / comp.Height;
            player.multistream().aspect = aspect;
            return { organizedStreams };
        });
    }
}
